import 'src/global.css';

import type { LoaderFunctionArgs } from 'react-router';

import { useRef, useEffect } from 'react';
import { useNavigate, useLoaderData } from 'react-router';

import { CONFIG } from 'src/global-config';
import { themeConfig, ThemeProvider } from 'src/theme';

import { SettingsProvider } from 'src/components/settings';
import { SplashScreen } from 'src/components/loading-screen';
import { MotionLazy } from 'src/components/animate/motion-lazy';

import { setSession } from 'src/auth/context/jwt/utils';

import axiosInstance, { endpoints } from '../lib/axios';

export type BridgeData = {
  success: boolean;
  token?: string;
  redirectPath: string;
  queryParams: string;
  error?: string;
};

export async function bridgeLoader({ request }: LoaderFunctionArgs): Promise<BridgeData> {
  const url = new URL(request.url);
  const pathname = url.pathname;
  const searchParams = url.search;

  const bridgePath = pathname.replace(/^\/bridge\/?/, '');
  const redirectPath = bridgePath ? `/${bridgePath}` : '/';

  await setSession(null);

  try {
    const res = await axiosInstance.post(endpoints.auth.validate, undefined, {
      withCredentials: true,
    });

    // Check for token in Authorization header first, then fallback to response body
    const authHeader = res.headers.authorization || res.headers.Authorization;
    const token = authHeader?.replace('Bearer ', '') || res.data?.id_token;

    if (!token) {
      throw new Error('No token received from authentication');
    }

    return {
      success: true,
      token,
      redirectPath,
      queryParams: searchParams,
    };
  } catch (error) {
    console.error('Bridge authentication failed:', error);
    return {
      success: false,
      redirectPath,
      queryParams: searchParams,
      error: error instanceof Error ? error.message : 'Authentication failed',
    };
  }
}

export default function Bridge() {
  const data = useLoaderData() as BridgeData;
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;

    if (data.success && data.token) {
      try {
        const redirectPathWithQueryParams = data.redirectPath + data.queryParams;
        navigate(redirectPathWithQueryParams, { replace: true });
      } catch (error) {
        console.error('Error during navigation:', error);
        window.location.href = CONFIG.externalLoginUrl;
      }
    } else {
      window.location.href = CONFIG.externalLoginUrl;
    }
  }, [data.success, data.token, data.redirectPath, data.queryParams, navigate]);

  return (
    <SettingsProvider>
      <ThemeProvider
        modeStorageKey={themeConfig.modeStorageKey}
        defaultMode={themeConfig.defaultMode}
      >
        <MotionLazy>
          <SplashScreen />
        </MotionLazy>
      </ThemeProvider>
    </SettingsProvider>
  );
}
